import { FC } from "react";
import {
  Box,
  Container,
  Flex,
  Grid,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { HomeContent } from "@/constants/landingPages";

const featureGradients: Record<string, string> = {
  "white-label": "linear(to-br, purple.50, pink.50)",
  "any-type": "linear(to-br, green.50, emerald.50)",
  "flexible-design": "linear(to-br, blue.50, cyan.50)",
  "custom-features": "linear(to-br, orange.50, yellow.50)",
  "premium-experience": "linear(to-br, teal.50, green.50)",
  "simple-deploy": "linear(to-br, indigo.50, purple.50)",
  "more-bookings": "linear(to-br, pink.50, red.50)",
  "stand-out": "linear(to-br, amber.50, yellow.50)",
};

const featureIcons: Record<string, string> = {
  "white-label": "✨",
  "any-type": "📅",
  "flexible-design": "🎨",
  "custom-features": "🚀",
  "premium-experience": "💎",
  "simple-deploy": "🔌",
  "more-bookings": "📈",
  "stand-out": "⭐",
};

interface FeaturesGridProps {
  content: HomeContent["features"];
}

export const FeaturesGrid: FC<FeaturesGridProps> = ({ content }) => (
  <Box py={{ base: "16", md: "24" }} bg="white">
    <Container maxW="7xl">
      <VStack spacing="16">
        {/* Section header */}
        <VStack spacing="4" textAlign="center" maxW="800px" mx="auto">
          <Heading as="h2" textStyle="heading36" color="gray.900">
            <Text
              as="span"
              bgGradient="linear(to-r, primary, pink.400)"
              bgClip="text"
            >
              {content.title.part1}
            </Text>{" "}
            {content.title.part2}
          </Heading>
          <Text textStyle="heading20" color="gray.600" lineHeight="1.7">
            {content.subtitle}
          </Text>
        </VStack>

        {/* Features grid */}
        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          }}
          gap="8"
          w="100%"
        >
          {content.items.map((feature) => (
            <ScrollReveal key={feature.id}>
              <Box
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                p="8"
                position="relative"
                overflow="hidden"
                transition="all 0.3s"
                minH={{ base: "250px", md: "340px" }}
                display="flex"
                flexDirection="column"
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: "xl",
                  borderColor: "gray.300",
                }}
              >
                {/* Gradient background */}
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  bgGradient={featureGradients[feature.id] || "linear(to-br, gray.50, gray.100)"}
                  opacity="0.5"
                  transition="opacity 0.3s"
                  _groupHover={{ opacity: 0.7 }}
                />

                <VStack align="flex-start" spacing="4" position="relative" zIndex={1}>
                  <Flex
                    w="14"
                    h="14"
                    align="center"
                    justify="center"
                    borderRadius="xl"
                    bg="white"
                    fontSize="3xl"
                    boxShadow="md"
                  >
                    {featureIcons[feature.id] || "📦"}
                  </Flex>

                  <VStack align="flex-start" spacing="2">
                    <Heading as="h3" textStyle="heading24" color="gray.900">
                      {feature.title}
                    </Heading>
                    <Text color="gray.600" lineHeight="1.7" fontSize="md">
                      {feature.description}
                    </Text>
                  </VStack>
                </VStack>
              </Box>
            </ScrollReveal>
          ))}
        </Grid>

        {/* Bottom CTA */}
        <Flex
          bg="gray.900"
          color="white"
          borderRadius="2xl"
          p={{ base: "8", md: "12" }}
          textAlign="center"
          w="100%"
          position="relative"
          overflow="hidden"
          minH={{ base: "250px", md: "initial" }}
          alignItems="center"
          m="auto"
        >
          <Box
            position="absolute"
            top="-50%"
            right="-10%"
            width="400px"
            height="400px"
            borderRadius="full"
            bgGradient="linear(to-br, primary, pink.500)"
            opacity="0.15"
            filter="blur(60px)"
          />
          <VStack spacing="4" position="relative" zIndex={1} m="auto">
            <Heading as="h3" textStyle="heading36" maxW="600px">
              <Text
                as="span"
                bgGradient="linear(to-r, primary, pink.400)"
                bgClip="text"
              >
                {content.bottomCta.title.part1}
              </Text>
              {" "}{content.bottomCta.title.part2}
            </Heading>
            <Text fontSize="lg" color="gray.300" maxW="700px">
              {content.bottomCta.description}
            </Text>
          </VStack>
        </Flex>
      </VStack>
    </Container>
  </Box>
);
